import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    IconButton, 
    Menu, 
    MenuItem, 
    Typography, 
    Box,
    ListItemIcon,
    Divider
} from '@mui/material';
import { IconLanguage, IconCheck } from '@tabler/icons-react';
import { colors } from '../ColorFont/color';

const languages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
    { code: 'mr', name: 'Marathi', nativeName: 'मराठी' }
];

const LanguageSelector = () => {
    const { i18n, t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        handleClose();
    };

    const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

    return (
        <Box>
            <IconButton
                onClick={handleClick}
                size="large"
                sx={{
                    p: 1,
                    backgroundColor: open ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                <Box sx={{ 
                    backgroundColor: '#F4F6F8', 
                    borderRadius: '50%', 
                    p: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    border: open ? `2px solid ${colors.primary}` : '2px solid transparent',
                }}>
                    <IconLanguage size={24} color={open ? colors.primary : "#919EAB"} stroke={2} />
                </Box>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                        mt: 1.5,
                        borderRadius: '12px',
                        minWidth: '180px',
                        backgroundColor: '#FFFFFF',
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: '#FFFFFF',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="overline" sx={{ fontWeight: 700, color: '#919EAB' }}>
                        {t('selectLanguage')}
                    </Typography>
                </Box>
                <Divider sx={{ mb: 1, borderStyle: 'dashed' }} />
                
                {languages.map((lang) => (
                    <MenuItem 
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        selected={i18n.language === lang.code}
                        sx={{
                            py: 1.5,
                            mx: 1,
                            borderRadius: '8px',
                            '&.Mui-selected': {
                                backgroundColor: colors.primary_rgba_8,
                                '&:hover': {
                                    backgroundColor: colors.primary_rgba_12,
                                }
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                             <Typography sx={{ fontWeight: 600, fontSize: '14px', color: i18n.language === lang.code ? colors.primary : '#1C252E' }}>
                                {lang.nativeName}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#919EAB' }}>
                                {lang.name}
                            </Typography>
                        </Box>
                        {i18n.language === lang.code && (
                            <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                                <IconCheck size={18} color={colors.primary} stroke={3} />
                            </ListItemIcon>
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default LanguageSelector;